import React from 'react';
import { useTranslation } from 'react-i18next';
import { PiArrowLeftLight, PiPhoneLight, PiVideoLight,  PiDotsThreeVerticalLight } from 'react-icons/pi';
import { GrGallery } from "react-icons/gr";
import ProfileImage from '../Header/ProfileImage';
import { Menu, Transition } from '@headlessui/react';
import { normalise } from '../shared/access/accessModel';

interface NewChatHeaderProps {
  selectedChat: any;
  onOpenGallery: () => void;
  handleStartCall: (val: boolean) => void;
  onBack: () => void;
  setShowChatList: (show: boolean) => void;
  showChatList: boolean;
  chatList: any[];
}

const NewChatHeader: React.FC<NewChatHeaderProps> = ({
  selectedChat,
  onOpenGallery,
  handleStartCall,
  onBack,
  setShowChatList,
  showChatList,
}) => {
  const { t } = useTranslation();

  const getDisplayName = () => {
    if (selectedChat.isGroup) {
      return selectedChat.GroupData?.Name || t('Group');
    }
    return `${selectedChat.userData?.FirstName} ${selectedChat.userData?.LastName}`.trim();
  };

  const getDisplayStatus = () => {
    if (selectedChat.isGroup) {
      const members = selectedChat.GroupData?.Members || [];
      return `${members.length} members`;
    }
    return selectedChat.userData?.Role || t('Online');
  };

  const getProfileImage = () => {
    if (selectedChat.isGroup) {
      return null;
    }
    return (
      <ProfileImage
        profilePicture={normalise(selectedChat.userData).avatar}
        email={selectedChat.userData?.Email}
        initials={
          selectedChat.userData?.Initials ||
          `${selectedChat.userData?.FirstName?.[0] || ''}${selectedChat.userData?.LastName?.[0] || ''}`
        }
        name={getDisplayName()}
        firstName={selectedChat.userData?.FirstName}
        lastName={selectedChat.userData?.LastName}
        size="h-10 w-10"
      />
    );
  };

  return (
    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
      {/* Left side - Back button and profile info */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <button
          onClick={onBack}
          className="md:hidden p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
        >
          <PiArrowLeftLight className="text-xl text-gray-700 dark:text-gray-300" />
        </button>

        <div className="flex items-center gap-3 min-w-0">
          {getProfileImage()}
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">{getDisplayName()}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{getDisplayStatus()}</p>
          </div>
        </div>
      </div>

      {/* Right side - Action buttons */}
      <div className="flex items-center gap-2 ml-4">
        <button
          onClick={() => handleStartCall(true)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          title={t("Start call")}
        >
          <PiPhoneLight className="text-xl" />
        </button>

        <button
          onClick={() => handleStartCall(true)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          title={t("Start video call")}
        >
          <PiVideoLight className="text-xl" />
        </button>

        <button
          onClick={onOpenGallery}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          title={t("Open gallery")}
        >
          <GrGallery className="text-xl" />
        </button>

        {/* Menu */}
        <Menu as="div" className="relative">
          <Menu.Button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
            <PiDotsThreeVerticalLight className="text-xl" />
          </Menu.Button>

          <Transition
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-10">
              <div className="p-2">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        active
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {t('View Details')}
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        active
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {t('Search Messages')}
                    </button>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </div>
  );
};

export default NewChatHeader;
